/**
 * One detector answer, read as a face shape: the single function a camera frame and a photo both pass through.
 *
 * It exists so the two inputs cannot drift. The camera path calls it for every frame; the photo path
 * calls it once. Neither adds a step the other lacks, so a photo is classified by exactly the
 * geometry, gates and rules a camera frame is — `@optiflow/face-vision`, unmodified.
 *
 * Faces are passed through as the detector found them. Zero or more than one is refused inside
 * face-vision by its own gates; nothing here picks the first or the largest face of several.
 */
import { analyseFaceShape, type FaceAnalysis } from '@optiflow/face-vision';
import { poseFromLandmarks, poseFromMatrix, type FaceLandmarks } from '@optiflow/pd-vision';

export function analyseDetection(
  faces: FaceLandmarks[],
  frameWidth: number,
  frameHeight: number,
): FaceAnalysis {
  const face = faces.length === 1 ? faces[0] : undefined;
  const pose = face
    ? ((face.matrix ? poseFromMatrix(face.matrix) : null) ??
      poseFromLandmarks(face.points, frameWidth, frameHeight))
    : null;

  return analyseFaceShape({
    faces,
    frameWidth,
    frameHeight,
    yawDeg: pose?.yawDeg ?? null,
    pitchDeg: pose?.pitchDeg ?? null,
  });
}
